import { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { TokenService } from '../../services/token.service';
import { SharedService } from '../../services/shared.service';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '../../services/core/appConfig.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

const REFRESH_URL = '/protocol/openid-connect/token';

export const tokenInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<any>> => {

  const tokenService = inject(TokenService);
  const sharedService = inject(SharedService);
  const translateService = inject(TranslateService);  
  const appConfig = inject(AppConfigService);

  // Evita errores si la config aún no se ha cargado, deja pasar la request
  if (!appConfig.getConfig()) {
    return next(req);
  }

  // Si se queda almacenado el token y refresh token expirado, para evitar que se quede colgado la web, borramos al completo.
  if (tokenService.tokenExpired() && tokenService.refreshTokenExpired()) {
    // Si por algún motivo, se queda el token 
    tokenService.clearTokenWithCookie();    
  }
  
  // Cuando se está referscando el token, se debe permitir la petición, en caso contrario, no funcionará.
  if (req.url.includes(REFRESH_URL)) {
    return next(req);
  }
  
  // Si el token es válido, añade cabeceras
  if (tokenService.isAuthenticate() ) {
    const authReq = addHeaders(req, tokenService.getAccesToken()!, translateService);
    return next(authReq);
  } 
  
  // Si el token está expirado pero el refresh token es válido
  if (tokenService.tokenExpired() && !tokenService.refreshTokenExpired()) {

    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);

      return tokenService.generateRefreshToken().pipe(
        switchMap((response: any) => {
          tokenService.setToken(response.access_token, response.refresh_token);
          refreshTokenSubject.next(response.access_token);
          return tokenService.obtainRoleUser(tokenService.obtainEmployeeNumber());
        }),
        switchMap((respo: any) => {
          if (respo.roles.includes('ROLE_ADMIN')) {
            sharedService.setAdminStatus(true);
            localStorage.setItem('isAdmin', 'true');
          }
          const authReq = addHeaders(req, tokenService.getAccesToken()!, translateService);
          return next(authReq);
        }),
        catchError((err) => {
          console.error('Error refrescando token', err);
          tokenService.clearTokenWithCookie();
          return throwError(() => err);
        }),
        finalize(() => {
          isRefreshing = false;
        })
      );
    } else {
      // Esperar si otro refresh está en curso
      return refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(newToken => {
          const authReq = addHeaders(req, newToken!, translateService);
          return next(authReq);
        })
      );
    }
  }
  // Si no hay token o no se puede refrescar, seguir sin modificar
  return next(req);
};

function addHeaders(req: HttpRequest<any>, token: string, translate: TranslateService): HttpRequest<any> {
  return req.clone({
    headers: req.headers
      .set('Authorization', `Bearer ${token}`)
      .set('Accept-Language', translate.currentLang || 'es')
  });

}